import { createDecorator } from 'vue-class-component';
import Vue from 'vue';
import { TranslateResult } from 'vue-i18n';

type IActionResult = 'Save' | 'Discard';

interface IUnsavedChangesDecoratorOptions<V> {
  messages?: {
    saveAndContinue?: string | TranslateResult;
    discardAndContinue?: string | TranslateResult;
    entity?: string | TranslateResult;
  };
  saveAndContinue?: (this: V) => boolean | Promise<boolean>;
  discardAndContinue?: (this: V) => void | Promise<void>;
}

function promptForAction<V>(component: Vue, options: IUnsavedChangesDecoratorOptions<V>): Promise<IActionResult> {
  return new Promise((resolve) => {
    const saveMessage = options.messages?.saveAndContinue || component.$t('PopupButton.yesSave');
    const discardMessage = options.messages?.discardAndContinue || component.$t('PopupButton.noDiscard');
    const entity = options.messages?.entity || component.$t('Order.order');

    component.$sPopup.alert(``, {
      title: component.$t('PopupTitle.beforeLeaving'),
      message: component.$t('PopupBody.unsavedChanges', { entity, action: component.$t('Document.Actions.leaving') }),
      buttons: [
        {
          text: saveMessage,
          color: 'primary',
          click: () => {
            resolve('Save');
          },
        },
        {
          text: discardMessage,
          click: () => {
            resolve('Discard');
          },
        },
      ],
    });
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isPromise(obj: any): obj is Promise<any> {
  return obj instanceof Promise || (obj && typeof obj.then === 'function');
}

export const registeredHandlers: ((this: Window, event: BeforeUnloadEvent) => unknown)[] = [];

export function UnsavedChanges<V extends Vue>(options: IUnsavedChangesDecoratorOptions<V>) {
  return function(target: Vue, key: string) {
    createDecorator((componentOptions, key) => {
      /* eslint-disable no-console */
      if (componentOptions.beforeCreate) console.warn(`Component ${componentOptions.name} already uses beforeCreate hook that will be overwritten by @UnsavedChanges decorator.`);
      if (componentOptions.beforeDestroy) console.warn(`Component ${componentOptions.name} already uses beforeDestroy hook that will be overwritten by @UnsavedChanges decorator.`);
      if (componentOptions.beforeRouteLeave) console.warn(`Component ${componentOptions.name} already uses beforeRouteLeave hook that will be overwritten by @UnsavedChanges decorator.`);
      /* eslint-enable no-console */

      componentOptions.beforeCreate = function(this: Vue) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const component = this as any;
        const handler = function(event: BeforeUnloadEvent) {
          const value: boolean | (() => boolean) = component[key];
          const hasUnsavedChanges = typeof value === 'function' ? value() : value;
          if (hasUnsavedChanges) {
            // chrome requires returnValue to be set as empty string :(
            event.returnValue = '';
            event.preventDefault();
          }
          // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
          // Returning empty string for legacy support. Otherwise, will not stop the event.
          return hasUnsavedChanges ? '' : null;
        };
        registeredHandlers.push(handler);
        window.addEventListener(`beforeunload`, handler);
      };

      componentOptions.beforeDestroy = function(this: Vue) {
        window.onbeforeunload = null;
      };

      componentOptions.beforeRouteLeave = function(this: V, from, to, next) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const value: boolean | (() => boolean) = (this as any)[key];
        const hasUnsavedChanges = typeof value === 'function' ? value() : value;
        if (hasUnsavedChanges) {
          const action = promptForAction(this, options);
          action.then(result => {
            if (result === 'Save') {
              if (options.saveAndContinue) {
                const returnValue = options.saveAndContinue.apply(this);
                if (isPromise(returnValue)) {
                  returnValue.then(r => next(r === false ? r : undefined));
                } else {
                  next();
                }
              } else {
                next();
              }
            } else if (result === 'Discard') {
              if (options.discardAndContinue) {
                const returnValue = options.discardAndContinue.apply(this);
                if (isPromise(returnValue)) {
                  returnValue.then(() => next());
                } else {
                  next();
                }
              } else {
                next();
              }
            } else {
              next();
            }
          });
          return;
        }
        next();
      };
    })(target, key);
  };
}
